<template>
  <!-- Floating help dialog (Teleport keeps it above app chrome). -->
  <Teleport to="body">
    <div v-if="helpStore.modalOpen" class="help-shell eh-popup-overlay" aria-live="polite">
      <div class="help-modal eh-popup-panel" role="dialog" aria-modal="true" aria-labelledby="help-modal-title">
        <div class="help-modal-header">
          <div class="help-modal-heading">
            <input
              v-if="editMode && canEditPlatform"
              v-model="draft.title"
              type="text"
              class="form-control help-title-input"
              placeholder="Help title"
            />
            <h3 v-else id="help-modal-title">{{ modalTitle }}</h3>
            <span class="help-id-badge">ID: {{ helpStore.activeHelpId }}</span>
          </div>
          <div class="help-header-actions">
            <button
              v-if="canEditAnything && !editMode"
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="startEdit"
            >
              Edit
            </button>
            <button type="button" class="help-close-btn" aria-label="Close help" @click="closeModal">
              ×
            </button>
          </div>
        </div>

        <div v-if="displayTabs.length" class="help-tabs" role="tablist">
          <button
            v-for="tab in displayTabs"
            :key="tab.key"
            type="button"
            role="tab"
            class="help-tab"
            :class="{ active: helpStore.activeTab === tab.key }"
            :aria-selected="helpStore.activeTab === tab.key"
            @click="helpStore.selectTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="help-modal-body">
          <template v-if="!editMode">
            <div v-if="activeViewHtml" class="help-html-content" v-html="activeViewHtml" />
            <p v-else-if="helpStore.activeTab === 'org'" class="help-unavailable">
              No {{ orgSpecificLabel }} guidance has been added yet.
            </p>
            <p v-else-if="helpStore.activeTab === 'role'" class="help-unavailable">
              No role-specific guidance has been added yet for your account type.
            </p>
            <p v-else class="help-unavailable">Help content unavailable.</p>
          </template>

          <template v-else>
            <!-- Org tab: HELP_MANAGE_ORG (help:admin) -->
            <div v-if="helpStore.activeTab === 'org' && canEditOrg" class="edit-section">
              <label class="edit-label">{{ orgSpecificLabel }} content</label>
              <EditorComponent v-model="draft.org_html" editorStyle="height: 200px" />
            </div>

            <!-- Platform tabs: a platform operator holding HELP_MANAGE_PLATFORM -->
            <div v-else-if="helpStore.activeTab === 'overview' && canEditPlatform" class="edit-section">
              <label class="edit-label">Overview / Context</label>
              <EditorComponent v-model="draft.overview_html" editorStyle="height: 220px" />
            </div>

            <div v-else-if="helpStore.activeTab === 'role' && canEditPlatform" class="edit-section">
              <p class="edit-hint">Each account type can have its own Role-Specific tab content.</p>
              <div v-for="role in roleFields" :key="role.key" class="role-edit-block">
                <label class="edit-label">{{ role.label }}</label>
                <EditorComponent v-model="draft.roles[role.key]" editorStyle="height: 140px" />
              </div>
            </div>

            <div v-else-if="helpStore.activeTab === 'details' && canEditPlatform" class="edit-section">
              <label class="edit-label">Details</label>
              <EditorComponent v-model="draft.details_html" editorStyle="height: 220px" />
            </div>

            <div v-else-if="helpStore.activeTab === 'howto' && canEditPlatform" class="edit-section">
              <label class="edit-label">How-To</label>
              <EditorComponent v-model="draft.how_to_html" editorStyle="height: 220px" />
            </div>
          </template>
        </div>

        <div v-if="editMode" class="help-modal-footer">
          <button type="button" class="btn btn-secondary" :disabled="saving" @click="cancelEdit">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" :disabled="saving" @click="saveEdits">
            {{ saving ? "Saving…" : "Save" }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { mapGetters } from "vuex";
import { HELP_ROLE_FIELDS, hasMeaningfulHtml, useHelpContentStore } from "@/stores/helpContentStore";
import { useOrganizationStore } from "@/stores/organizationStore";
import { usePrivilegesStore } from "@/stores/privileges";
import { useSessionStore } from "@/stores/session";
import { PRIVILEGES } from "@/constants/privileges";

function emptyDraft() {
  return {
    title: "",
    overview_html: "",
    details_html: "",
    how_to_html: "",
    org_html: "",
    roles: { provider: "", nurse: "", patient: "", administrator: "" },
  };
}

export default {
  name: "HelpModal",
  data() {
    return {
      editMode: false,
      saving: false,
      draft: emptyDraft(),
      roleFields: HELP_ROLE_FIELDS,
    };
  },
  computed: {
    ...mapGetters("login", { allData: "getAllData", isLoggedIn: "isLoggedIn" }),
    helpStore() {
      return useHelpContentStore();
    },
    orgSpecificLabel() {
      return useOrganizationStore().orgSpecificLabel;
    },
    accountnumber() {
      return this.allData?.manager?.externid2 || this.allData?.patient?.externId2 || "";
    },
    actorId() {
      return this.allData?.manager?.externid || this.allData?.patient?.externId || null;
    },
    /**
     * The platform tabs (Overview, Role-Specific, Details, How-To) write the
     * article every tenant reads, so the API takes that write only from a
     * platform operator holding help:admin. Main offered them to every
     * administrator because its write checked nothing; here a tenant
     * administrator would be handed an editor whose every save is refused.
     * `/me` says who is an operator, read live from `users`.
     */
    canEditPlatform() {
      return (
        Boolean(useSessionStore().me?.is_platform_operator) &&
        usePrivilegesStore().effectiveGranted(PRIVILEGES.HELP_MANAGE_PLATFORM)
      );
    },
    /**
     * The organization's own tab needs help:admin alone (administrators hold
     * it through their group). Not the account type: an administrator whose
     * grant was narrowed would be offered a save the API refuses.
     */
    canEditOrg() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.HELP_MANAGE_ORG);
    },
    canEditAnything() {
      return this.canEditPlatform || this.canEditOrg;
    },
    modalTitle() {
      return this.helpStore.activeArticle?.title || "Help";
    },
    /** Edit mode only shows tabs the user may write to. */
    editTabs() {
      const tabs = [];
      if (this.canEditPlatform) tabs.push({ key: "overview", label: "Overview/Context" });
      if (this.canEditOrg) tabs.push({ key: "org", label: this.orgSpecificLabel });
      if (this.canEditPlatform) {
        tabs.push(
          { key: "role", label: "Role-Specific" },
          { key: "details", label: "Details" },
          { key: "howto", label: "How-To" }
        );
      }
      return tabs;
    },
    displayTabs() {
      return this.editMode ? this.editTabs : this.helpStore.activeTabs;
    },
    activeViewHtml() {
      return this.helpStore.activeTabContent;
    },
  },
  // Modal is mounted on App (survives layout/route swaps); close it when context leaves.
  watch: {
    // Any in-app navigation should dismiss help so it doesn't float over the next page.
    "$route.fullPath"() {
      if (this.helpStore.modalOpen) this.closeModal();
    },
    // Logout / session clear flips isLoggedIn; don't leave help open on the login screen.
    isLoggedIn(next, prev) {
      if (prev && !next && this.helpStore.modalOpen) this.closeModal();
    },
  },
  methods: {
    closeModal() {
      this.cancelEdit();
      this.helpStore.closeHelp();
    },

    /** Copy current store content into the editable draft. */
    loadDraftFromStore() {
      const helpId = this.helpStore.activeHelpId;
      const article = this.helpStore.articlesById[helpId] || {};
      const roleRows = this.helpStore.roleSectionsByHelpId[helpId] || [];
      const roles = { provider: "", nurse: "", patient: "", administrator: "" };
      for (const field of this.roleFields) {
        const match = roleRows.find((r) => String(r.role_name).toLowerCase() === field.key);
        roles[field.key] = match?.body_html || "";
      }
      this.draft = {
        title: article.title || helpId,
        overview_html: article.overview_html || "",
        details_html: article.details_html || "",
        how_to_html: article.how_to_html || "",
        org_html: this.helpStore.orgSectionsByHelpId[helpId] || "",
        roles,
      };
    },

    startEdit() {
      this.loadDraftFromStore();
      this.editMode = true;
      const tabs = this.editTabs;
      if (tabs.length && !tabs.find((t) => t.key === this.helpStore.activeTab)) {
        this.helpStore.selectTab(tabs[0].key);
      }
    },

    cancelEdit() {
      this.editMode = false;
      this.saving = false;
      this.draft = emptyDraft();
    },

    async saveEdits() {
      this.saving = true;
      try {
        if (this.canEditOrg) {
          await this.helpStore.saveOrgSection(
            this.accountnumber,
            this.helpStore.activeHelpId,
            this.draft.org_html,
            this.actorId
          );
        }
        if (this.canEditPlatform) {
          await this.helpStore.savePlatformArticle({
            help_id: this.helpStore.activeHelpId,
            title: this.draft.title || this.helpStore.activeHelpId,
            overview_html: this.draft.overview_html,
            details_html: this.draft.details_html,
            how_to_html: this.draft.how_to_html,
            role_sections: this.roleFields
              .filter((r) => hasMeaningfulHtml(this.draft.roles[r.key]))
              .map((r) => ({ role_name: r.key, body_html: this.draft.roles[r.key] })),
          });
        }
        this.editMode = false;
        const tabs = this.helpStore.activeTabs;
        if (tabs.length && !tabs.find((t) => t.key === this.helpStore.activeTab)) {
          this.helpStore.selectTab(tabs[0].key);
        }
      } catch (err) {
        alert(err?.message || "Unable to save help content.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.help-shell {
  /* Backdrop from brand-ds (.eh-popup-overlay). z-index stays above --eh-popup-z-index
     so help opened from another dialog remains on top. */
  position: fixed;
  inset: 0;
  z-index: 2000100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.help-modal {
  /* Surface, border, radius from brand-ds (.eh-popup-panel / --eh-popup-*). */
  width: min(760px, 96vw);
  max-height: min(85vh, 800px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.help-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid #e8edf3;
}

.help-modal-heading h3 {
  margin: 0 0 0.35rem;
  font-size: 1.15rem;
}

.help-title-input {
  max-width: 360px;
  margin-bottom: 0.35rem;
  font-weight: 600;
}

.help-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.help-id-badge {
  display: inline-block;
  font-size: 0.72rem;
  color: #667085;
  background: #f4f6f8;
  border-radius: 4px;
  padding: 0.15rem 0.45rem;
}

.help-close-btn {
  border: none;
  background: transparent;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #444;
  padding: 0 0.25rem;
}

/* Underline tabs (not pill buttons). */
.help-tabs {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 0;
  min-height: 2.75rem;
  padding: 0 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  overflow-x: auto;
  overflow-y: hidden;
}

.help-tab {
  flex: 0 0 auto;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #64748b;
  border-radius: 0;
  margin-bottom: -1px;
  padding: 0.85rem 0.9rem;
  font-size: 0.86rem;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #1e293b;
  }

  &.active {
    color: #0060ff;
    border-bottom-color: #0060ff;
  }

  &:focus-visible {
    outline: 2px solid #0060ff;
    outline-offset: -2px;
  }
}

.help-modal-body {
  padding: 1rem 1.25rem;
  overflow: auto;
  flex: 1;
}

.help-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem 1rem;
  border-top: 1px solid #e8edf3;
}

.help-html-content {
  font-size: 0.95rem;
  line-height: 1.55;
  color: #1f2937;

  :deep(p) {
    margin: 0 0 0.75rem;
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 0.75rem 1.25rem;
  }
}

.help-unavailable {
  margin: 0;
  color: #667085;
  font-style: italic;
}

.edit-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.edit-hint {
  color: #667085;
  font-size: 0.9rem;
  margin: 0 0 0.75rem;
}

.role-edit-block {
  margin-bottom: 1rem;
}
</style>
